'use client';

import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

interface ServiceHealth {
  name: string;
  status: 'healthy' | 'degraded' | 'down';
  latency: number;
  uptime: number;
  lastCheck: string;
  errorRate: number;
}

interface ErrorLog {
  id: string;
  level: 'error' | 'warning' | 'info';
  message: string;
  source: string;
  time: string;
  count: number;
}

const INITIAL_SERVICES: ServiceHealth[] = [
  { name: 'NHL Official API', status: 'healthy', latency: 142, uptime: 99.8, lastCheck: '5s ago', errorRate: 0.2 },
  { name: 'ML Engine', status: 'healthy', latency: 89, uptime: 99.9, lastCheck: '3s ago', errorRate: 0.1 },
  { name: 'Supabase DB', status: 'healthy', latency: 45, uptime: 100, lastCheck: '2s ago', errorRate: 0.0 },
  { name: 'Odds Feed API', status: 'degraded', latency: 891, uptime: 96.2, lastCheck: '12s ago', errorRate: 3.8 },
  { name: 'Injury Reports', status: 'healthy', latency: 312, uptime: 98.4, lastCheck: '8s ago', errorRate: 1.6 },
  { name: 'Cache Layer', status: 'healthy', latency: 12, uptime: 100, lastCheck: '1s ago', errorRate: 0.0 },
  { name: 'AI Copilot (OpenAI)', status: 'healthy', latency: 1240, uptime: 99.5, lastCheck: '15s ago', errorRate: 0.5 },
  { name: 'Stripe Billing', status: 'healthy', latency: 234, uptime: 99.9, lastCheck: '30s ago', errorRate: 0.1 },
];

const ERROR_LOGS: ErrorLog[] = [
  { id: '1', level: 'warning', message: 'Odds API response time > 800ms threshold', source: 'OddsService', time: '2m ago', count: 14 },
  { id: '2', level: 'info', message: 'Cache miss rate elevated: 23% (threshold: 20%)', source: 'CacheLayer', time: '5m ago', count: 3 },
  { id: '3', level: 'error', message: 'NHL API rate limit approached: 87% of quota used', source: 'NHLClient', time: '12m ago', count: 1 },
  { id: '4', level: 'info', message: 'ML model retrained successfully. Accuracy: 75.3%', source: 'MLEngine', time: '1h ago', count: 1 },
  { id: '5', level: 'warning', message: 'Injury report data stale > 10 minutes', source: 'InjuryService', time: '2h ago', count: 2 },
];

const MISSING_DATA = [
  { field: 'Gardien confirmé', teams: ['ARI', 'CBJ', 'BUF'], severity: 'high' },
  { field: 'Alignement complet', teams: ['DET', 'OTT'], severity: 'medium' },
  { field: 'Cotes live', teams: ['SEA', 'NSH', 'MIN'], severity: 'low' },
  { field: 'Stats power play', teams: ['SJS'], severity: 'low' },
];

export default function HealthDashboardClient() {
  const [services, setServices] = useState<ServiceHealth[]>(INITIAL_SERVICES);
  const [lastRefresh, setLastRefresh] = useState('Just now');
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      setServices((prev) =>
        prev.map((s) => ({
          ...s,
          latency: Math.max(10, s.latency + Math.floor((Math.random() - 0.5) * 40)),
          lastCheck: 'Just now',
        }))
      );
      setLastRefresh('Just now');
    }, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const healthyCount = services.filter((s) => s.status === 'healthy').length;
  const degradedCount = services.filter((s) => s.status === 'degraded').length;
  const downCount = services.filter((s) => s.status === 'down').length;
  const overallStatus = downCount > 0 ? 'INCIDENT' : degradedCount > 0 ? 'DÉGRADÉ' : 'OPÉRATIONNEL';

  return (
    <AppLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Icon name="ServerIcon" size={22} className="text-primary" />
              System Health Dashboard
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">Monitoring temps réel · APIs · Cache · Données manquantes</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${autoRefresh ? 'border-positive/30 bg-positive-subtle text-positive' : 'border-border text-muted-foreground'}`}
            >
              <Icon name="ArrowPathIcon" size={14} className={autoRefresh ? 'animate-spin' : ''} />
              Auto-refresh
            </button>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-xs font-bold ${
              overallStatus === 'OPÉRATIONNEL' ? 'border-positive/20 bg-positive-subtle text-positive' :
              overallStatus === 'DÉGRADÉ'? 'border-warning/20 bg-warning/10 text-warning' : 'border-negative/20 bg-negative-subtle text-negative'
            }`}>
              <span className={`w-2 h-2 rounded-full ${overallStatus === 'OPÉRATIONNEL' ? 'bg-positive pulse-dot' : overallStatus === 'DÉGRADÉ' ? 'bg-warning' : 'bg-negative'}`} />
              {overallStatus}
            </div>
          </div>
        </div>

        {/* Summary KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Services OK', value: healthyCount, total: services.length, color: 'text-positive', icon: 'CheckCircleIcon' },
            { label: 'Dégradés', value: degradedCount, total: services.length, color: 'text-warning', icon: 'ExclamationTriangleIcon' },
            { label: 'Incidents', value: downCount, total: services.length, color: 'text-negative', icon: 'XCircleIcon' },
            { label: 'Erreurs actives', value: ERROR_LOGS.filter((e) => e.level === 'error').length, total: ERROR_LOGS.length, color: 'text-negative', icon: 'BugAntIcon' },
          ].map((kpi) => (
            <div key={kpi.label} className="card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name={kpi.icon as any} size={16} className={kpi.color} />
                <span className="text-xs text-muted-foreground">{kpi.label}</span>
              </div>
              <div className={`text-2xl font-bold font-mono-data ${kpi.color}`}>{kpi.value}</div>
              <div className="text-xs text-muted-foreground mt-1">sur {kpi.total} services</div>
            </div>
          ))}
        </div>

        {/* Services Grid */}
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">État des Services</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {services.map((svc) => (
              <div key={svc.name} className={`flex items-center gap-4 p-4 rounded-xl border ${
                svc.status === 'healthy' ? 'border-border bg-background' :
                svc.status === 'degraded'? 'border-warning/20 bg-warning/5' : 'border-negative/20 bg-negative-subtle/10'
              }`}>
                <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                  svc.status === 'healthy' ? 'bg-positive pulse-dot' :
                  svc.status === 'degraded' ? 'bg-warning' : 'bg-negative'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{svc.name}</div>
                  <div className="text-xs text-muted-foreground">Vérifié : {svc.lastCheck}</div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-right text-xs">
                  <div>
                    <div className="text-muted-foreground">Latence</div>
                    <div className={`font-mono-data font-medium ${svc.latency > 500 ? 'text-warning' : svc.latency > 200 ? 'text-foreground' : 'text-positive'}`}>{svc.latency}ms</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Uptime</div>
                    <div className={`font-mono-data font-medium ${svc.uptime >= 99 ? 'text-positive' : svc.uptime >= 97 ? 'text-warning' : 'text-negative'}`}>{svc.uptime}%</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Erreurs</div>
                    <div className={`font-mono-data font-medium ${svc.errorRate === 0 ? 'text-positive' : svc.errorRate < 2 ? 'text-warning' : 'text-negative'}`}>{svc.errorRate}%</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Missing Data Monitor */}
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
            <Icon name="ExclamationTriangleIcon" size={16} className="text-warning" />
            Données Manquantes — Monitoring
          </h3>
          <div className="space-y-2">
            {MISSING_DATA.map((item) => (
              <div key={item.field} className={`flex items-center gap-4 p-3 rounded-xl border ${
                item.severity === 'high' ? 'border-negative/20 bg-negative-subtle/10' :
                item.severity === 'medium'? 'border-warning/20 bg-warning/5' : 'border-border bg-background'
              }`}>
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${item.severity === 'high' ? 'bg-negative' : item.severity === 'medium' ? 'bg-warning' : 'bg-muted-foreground'}`} />
                <div className="flex-1">
                  <span className="text-sm font-medium text-foreground">{item.field}</span>
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {item.teams.map((t) => (
                    <span key={t} className="text-2xs px-2 py-0.5 rounded bg-card border border-border text-muted-foreground font-mono-data">{t}</span>
                  ))}
                </div>
                <span className={`text-2xs px-2 py-0.5 rounded font-bold ${
                  item.severity === 'high' ? 'bg-negative-subtle text-negative' :
                  item.severity === 'medium'? 'bg-warning/10 text-warning' : 'bg-card text-muted-foreground'
                }`}>{item.severity.toUpperCase()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Error Logs */}
        <div className="card p-5">
          <h3 className="text-sm font-semibold text-foreground mb-4">Journal d'Erreurs Temps Réel</h3>
          <div className="space-y-2">
            {ERROR_LOGS.map((log) => (
              <div key={log.id} className={`flex items-start gap-3 p-3 rounded-xl border text-xs ${
                log.level === 'error' ? 'border-negative/20 bg-negative-subtle/10' :
                log.level === 'warning'? 'border-warning/20 bg-warning/5' : 'border-border bg-background'
              }`}>
                <Icon
                  name={log.level === 'error' ? 'XCircleIcon' : log.level === 'warning' ? 'ExclamationTriangleIcon' : 'InformationCircleIcon'}
                  size={14}
                  className={`mt-0.5 flex-shrink-0 ${log.level === 'error' ? 'text-negative' : log.level === 'warning' ? 'text-warning' : 'text-primary'}`}
                />
                <div className="flex-1 min-w-0">
                  <span className="text-foreground">{log.message}</span>
                  <div className="flex items-center gap-3 mt-1 text-muted-foreground">
                    <span className="font-mono-data">{log.source}</span>
                    <span>{log.time}</span>
                    {log.count > 1 && <span className="bg-card border border-border px-1.5 py-0.5 rounded">×{log.count}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
